import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ClipboardDocumentListIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { healthChecklistsApi, HealthChecklist } from '../../api/healthChecklists';

interface HealthChecklistsListProps {
  onInvestigateFromChecklist?: (birdId: string, birdLabel: string, concernItemIds: string[]) => void;
}

export default function HealthChecklistsList({ onInvestigateFromChecklist }: HealthChecklistsListProps) {
  const [checklists, setChecklists] = useState<HealthChecklist[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    healthChecklistsApi
      .getAll()
      .then(setChecklists)
      .catch(() => setChecklists([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-600" />
      </div>
    );
  }

  if (checklists.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 py-10 text-center text-sm text-slate-500">
        No health checklists recorded yet. Run a daily, weekly, or emergency checklist from Quick add.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {checklists
        .sort((a, b) => new Date(b.checklistDate).getTime() - new Date(a.checklistDate).getTime())
        .map((cl) => {
          const concerns = (cl.checklistItems ?? []).filter(
            (i) => i.status === 'CONCERN' || i.status === 'ISSUE',
          );
          const score = cl.healthScore?.score;
          return (
            <div key={cl.id} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <ClipboardDocumentListIcon className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-slate-900">
                      {cl.checklistType} checklist · {cl.birdId.slice(0, 8)}…
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    {format(new Date(cl.checklistDate), 'dd MMM yyyy')} · {cl.status}
                    {typeof score === 'number' ? ` · Score ${score}` : ''}
                  </p>
                  {concerns.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {concerns.map((c) => (
                        <span
                          key={c.id}
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            c.status === 'ISSUE' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {c.title}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {concerns.length > 0 && onInvestigateFromChecklist && (
                  <button
                    type="button"
                    onClick={() =>
                      onInvestigateFromChecklist(
                        cl.birdId,
                        cl.birdId,
                        concerns.map((c) => c.id),
                      )
                    }
                    className="btn btn-secondary text-xs"
                  >
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    Investigate symptoms
                  </button>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
}
